import {
  Alert,
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Group,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconArrowRight,
  IconCalculator,
  IconFileText,
  IconInfoCircle,
  IconSchool,
  IconSparkles,
} from "@tabler/icons-react";
import { ReactNode, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../../seo/Seo";
import {
  buildCanonicalUrl,
  createBreadcrumbSchema,
  createFaqSchema,
} from "../../seo/utils";
import "./SpeechTimeCalculatorPage.css";

type NumberValue = number | "";
type SpeechMode = "text" | "words" | "time";
type PresetSpeedKey = keyof typeof speakingSpeeds;
type SpeechSpeedKey = PresetSpeedKey | "custom";

const TOOL_PATH = "/tools/speech-time-calculator";
const TOOLS_PATH = "/tools";

export const speakingSpeeds = {
  slow: 100,
  normal: 130,
  conversational: 150,
  fast: 180,
};

const speedOptions: Array<{
  value: SpeechSpeedKey;
  label: string;
}> = [
  { value: "slow", label: "Slow - 100 words per minute" },
  { value: "normal", label: "Normal - 130 words per minute" },
  { value: "conversational", label: "Conversational - 150 words per minute" },
  { value: "fast", label: "Fast - 180 words per minute" },
  { value: "custom", label: "Custom speed" },
];

const speedLabels: Record<PresetSpeedKey, string> = {
  slow: "Slow pace",
  normal: "Normal pace",
  conversational: "Conversational pace",
  fast: "Fast pace",
};

const faqs = [
  {
    question: "How do I calculate speech time?",
    answer:
      "To calculate speech time, divide your total word count by your speaking speed. For example, 750 words divided by 150 words per minute equals 5 minutes.",
  },
  {
    question: "How many words are in a 5-minute speech?",
    answer:
      "A 5-minute speech is usually around 650 to 750 words at a normal speaking pace. Slow speakers may need around 500 words, while fast speakers may speak around 900 words.",
  },
  {
    question: "How many words are in a 3-minute speech?",
    answer:
      "A 3-minute speech is usually around 390 to 450 words at a normal speaking speed of 130 to 150 words per minute.",
  },
  {
    question: "How many words are in a 10-minute speech?",
    answer:
      "A 10-minute speech is usually around 1300 to 1500 words at a normal speaking speed.",
  },
  {
    question: "What is the average speaking speed?",
    answer:
      "Most people speak around 130 to 150 words per minute during speeches and presentations. Some speakers may speak slower or faster depending on confidence, pauses, and topic complexity.",
  },
  {
    question: "Is this the same as a speech timer?",
    answer:
      "This tool estimates speech time before you speak. A speech timer measures the actual time while you are delivering the speech. You can use this calculator to prepare your script before practicing with a timer.",
  },
  {
    question: "Can I paste my full speech into the calculator?",
    answer:
      "Yes. You can paste your full speech or presentation script, and the calculator will count the words and estimate the speaking time.",
  },
  {
    question: "Should I write fewer words if I have slides?",
    answer:
      "Yes. If your presentation includes slides, examples, pauses, or audience interaction, write slightly fewer words than the exact time limit to avoid running over time.",
  },
];

const wordsToMinutesRows = [
  ["300 words", "3 min", "2 min 18 sec", "2 min", "1 min 40 sec"],
  ["500 words", "5 min", "3 min 51 sec", "3 min 20 sec", "2 min 47 sec"],
  ["750 words", "7 min 30 sec", "5 min 46 sec", "5 min", "4 min 10 sec"],
  ["1000 words", "10 min", "7 min 42 sec", "6 min 40 sec", "5 min 33 sec"],
  ["1500 words", "15 min", "11 min 32 sec", "10 min", "8 min 20 sec"],
  ["2000 words", "20 min", "15 min 23 sec", "13 min 20 sec", "11 min 7 sec"],
];

const timeToWordsRows = [
  ["1 minute", "100 words", "130 words", "150 words", "180 words"],
  ["2 minutes", "200 words", "260 words", "300 words", "360 words"],
  ["3 minutes", "300 words", "390 words", "450 words", "540 words"],
  ["5 minutes", "500 words", "650 words", "750 words", "900 words"],
  ["10 minutes", "1000 words", "1300 words", "1500 words", "1800 words"],
  ["15 minutes", "1500 words", "1950 words", "2250 words", "2700 words"],
];

export function countWords(text: string) {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

export function formatSpeechTime(decimalMinutes: number) {
  const totalSeconds = Math.round(decimalMinutes * 60);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;

  if (mins === 0) return `${secs} sec`;
  if (secs === 0) return `${mins} min`;
  return `${mins} min ${secs} sec`;
}

export function calculateSpeechTime(wordCount: number, wpm: number) {
  if (!wordCount || !wpm || wordCount <= 0 || wpm <= 0) return null;
  return wordCount / wpm;
}

export function calculateWordsFromTime(
  minutes: number | "",
  seconds: number | "",
  wpm: number,
) {
  const totalMinutes = Number(minutes || 0) + Number(seconds || 0) / 60;
  if (!totalMinutes || !wpm || totalMinutes <= 0 || wpm <= 0) return null;
  return Math.round(totalMinutes * wpm);
}

function toNumberInputValue(value: string | number): NumberValue {
  if (value === "") {
    return "";
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : "";
}

function numberValue(value: NumberValue) {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function formatWords(value: number) {
  return Math.round(value).toLocaleString("en-US");
}

function getWordsPerMinute(speed: SpeechSpeedKey, customWpm: NumberValue) {
  if (speed === "custom") {
    return numberValue(customWpm) > 0 ? numberValue(customWpm) : null;
  }

  return speakingSpeeds[speed];
}

function getInputTimeLabel(minutes: NumberValue, seconds: NumberValue) {
  return formatSpeechTime(numberValue(minutes) + numberValue(seconds) / 60);
}

function SpeechTimeCalculatorPage() {
  const webApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Speech Time Calculator",
    applicationCategory: "EducationalApplication",
    operatingSystem: "All",
    url: buildCanonicalUrl(TOOL_PATH),
    description:
      "Free speech time calculator to convert words to minutes, estimate speech length, and calculate how many words are needed for a timed presentation.",
    isAccessibleForFree: true,
  };

  return (
    <Container fluid className="speech-time-page" px={0}>
      <Seo
        title="Speech Time Calculator | Convert Words to Minutes"
        description="Use our free Speech Time Calculator to find how long your speech will take. Convert words to minutes, paste your script, and estimate speaking time by speed."
        path={TOOL_PATH}
        ogTitle="Speech Time Calculator"
        ogDescription="Convert words to minutes, paste a script, or estimate how many words you need for a timed speech."
        keywords={[
          "speech time calculator",
          "speaking time calculator",
          "words to minutes",
          "speech length calculator",
          "how long is my speech",
          "text to speech time calculator",
          "word count speech time calculator",
          "speech timer",
        ]}
        schema={[
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Tools", path: TOOLS_PATH },
            { name: "Speech Time Calculator", path: TOOL_PATH },
          ]),
          createFaqSchema(faqs),
          webApplicationSchema,
        ]}
      />

      <Box className="speech-time-hero">
        <Container size="xl">
          <Stack gap="lg">
            <Breadcrumbs separator="/">
              <Anchor component={Link} to="/">
                Home
              </Anchor>
              <Anchor component={Link} to={TOOLS_PATH}>
                Tools
              </Anchor>
              <Text>Speech Time Calculator</Text>
            </Breadcrumbs>

            <SimpleGrid cols={{ base: 1, lg: 2 }} spacing={34}>
              <Stack gap="md" justify="center">
                <Group gap="sm">
                  <Badge color="green" radius="sm" size="lg">
                    Speech & Presentation Tools
                  </Badge>
                  <Badge color="blue" radius="sm" size="lg" variant="light">
                    Free words to minutes tool
                  </Badge>
                </Group>
                <ThemeIcon size={58} radius="md" color="green" variant="light">
                  <IconCalculator size={32} />
                </ThemeIcon>
                <Title order={1} className="speech-time-title">
                  Speech Time Calculator
                </Title>
                <Stack gap="xs" className="speech-time-intro">
                  <Text size="lg" c="dimmed">
                    Use our free Speech Time Calculator to find out how long
                    your speech, presentation, or script will take to deliver.
                    Paste your text or enter your word count, choose your
                    speaking speed, and instantly convert words to minutes.
                  </Text>
                  <Text c="dimmed">
                    Perfect for school speeches, college presentations, debates,
                    seminars, video scripts, and oral assignments.
                  </Text>
                </Stack>
              </Stack>

              <Paper
                withBorder
                radius="md"
                p={{ base: "md", md: "xl" }}
                className="speech-time-panel"
              >
                <SpeechTimeCalculator />
              </Paper>
            </SimpleGrid>
          </Stack>
        </Container>
      </Box>

      <Container size="xl" py={48}>
        <main className="speech-time-content">
          <SpeechTimeSeoContent />
          <SpeechTimeCta />
          <RelatedToolsSection />
          <SpeechTimeFAQ />
        </main>
      </Container>
    </Container>
  );
}

export function SpeechTimeCalculator() {
  const [mode, setMode] = useState<SpeechMode>("text");
  const [scriptText, setScriptText] = useState("");
  const [wordCountInput, setWordCountInput] = useState<NumberValue>("");
  const [minutesInput, setMinutesInput] = useState<NumberValue>(5);
  const [secondsInput, setSecondsInput] = useState<NumberValue>(0);
  const [speed, setSpeed] = useState<SpeechSpeedKey>("conversational");
  const [customWpm, setCustomWpm] = useState<NumberValue>(150);
  const wordsPerMinute = getWordsPerMinute(speed, customWpm);
  const textWordCount = countWords(scriptText);
  const numericWordCount = numberValue(wordCountInput);
  const totalTimeMinutes = numberValue(minutesInput) + numberValue(secondsInput) / 60;

  const result = useMemo(() => {
    if (!wordsPerMinute) {
      return null;
    }

    if (mode === "text") {
      const speechTime = calculateSpeechTime(textWordCount, wordsPerMinute);

      return speechTime
        ? {
            mode,
            wordCount: textWordCount,
            wordsPerMinute,
            formattedTime: formatSpeechTime(speechTime),
          }
        : null;
    }

    if (mode === "words") {
      const speechTime = calculateSpeechTime(numericWordCount, wordsPerMinute);

      return speechTime
        ? {
            mode,
            wordCount: numericWordCount,
            wordsPerMinute,
            formattedTime: formatSpeechTime(speechTime),
          }
        : null;
    }

    const estimatedWords = calculateWordsFromTime(
      minutesInput,
      secondsInput,
      wordsPerMinute,
    );

    return estimatedWords
      ? {
          mode,
          estimatedWords,
          wordsPerMinute,
          timeLabel: getInputTimeLabel(minutesInput, secondsInput),
        }
      : null;
  }, [
    minutesInput,
    mode,
    numericWordCount,
    secondsInput,
    textWordCount,
    wordsPerMinute,
  ]);

  const validationMessage = useMemo(() => {
    if (!wordsPerMinute) {
      return "Enter a positive custom words-per-minute speed to see the estimate.";
    }

    if (mode === "text" && textWordCount <= 0) {
      return "Paste your speech or script to see the speaking time estimate.";
    }

    if (mode === "words" && numericWordCount <= 0) {
      return "Enter a positive word count to convert words to minutes.";
    }

    if (mode === "time" && totalTimeMinutes <= 0) {
      return "Enter a positive speech time to estimate the word count.";
    }

    return "";
  }, [
    mode,
    numericWordCount,
    textWordCount,
    totalTimeMinutes,
    wordsPerMinute,
  ]);

  return (
    <Stack gap="lg" component="section" aria-label="Speech time calculator">
      <Stack gap={6}>
        <Title order={2}>Calculate Speech Time</Title>
        <Text c="dimmed">
          Paste a script, enter a word count, or plan backwards from a time
          limit.
        </Text>
      </Stack>

      <SegmentedControl
        fullWidth
        value={mode}
        onChange={(value) => setMode(value as SpeechMode)}
        className="speech-time-mode-control"
        data={[
          { value: "text", label: "Text to Speech Time" },
          { value: "words", label: "Words to Time" },
          { value: "time", label: "Time to Words" },
        ]}
        aria-label="Speech calculator mode"
      />

      {mode === "text" ? (
        <TextToSpeechTimeMode
          scriptText={scriptText}
          onScriptTextChange={setScriptText}
          wordCount={textWordCount}
        />
      ) : null}

      {mode === "words" ? (
        <WordsToTimeMode
          wordCount={wordCountInput}
          onWordCountChange={setWordCountInput}
        />
      ) : null}

      {mode === "time" ? (
        <TimeToWordsMode
          minutes={minutesInput}
          seconds={secondsInput}
          onMinutesChange={setMinutesInput}
          onSecondsChange={setSecondsInput}
        />
      ) : null}

      <SpeechSpeedSelector
        speed={speed}
        customWpm={customWpm}
        onSpeedChange={setSpeed}
        onCustomWpmChange={setCustomWpm}
      />

      {validationMessage ? (
        <Alert
          color="yellow"
          radius="md"
          variant="light"
          icon={<IconInfoCircle size={18} />}
        >
          {validationMessage}
        </Alert>
      ) : null}

      {result ? (
        <>
          <SpeechTimeResult result={result} />
          <SpeechSpeedComparison
            mode={mode}
            wordCount={mode === "text" ? textWordCount : numericWordCount}
            totalMinutes={totalTimeMinutes}
          />
          <Box className="speech-time-tip-box tip-box">
            <Group gap="md" align="flex-start" wrap="nowrap">
              <ThemeIcon color="blue" radius="md" variant="light">
                <IconSparkles size={20} />
              </ThemeIcon>
              <Stack gap={4}>
                <Title order={3}>Presentation Tip</Title>
                <Text c="dimmed">
                  If your speech includes slides, examples, pauses, or Q&A,
                  keep your written speech slightly shorter than the exact time
                  limit.
                </Text>
              </Stack>
            </Group>
          </Box>
        </>
      ) : null}
    </Stack>
  );
}

function TextToSpeechTimeMode({
  scriptText,
  onScriptTextChange,
  wordCount,
}: {
  scriptText: string;
  onScriptTextChange: (value: string) => void;
  wordCount: number;
}) {
  return (
    <Stack gap="sm">
      <Textarea
        label="Paste your speech or script"
        placeholder="Paste your speech, presentation script, or video script here..."
        value={scriptText}
        onChange={(event) => onScriptTextChange(event.currentTarget.value)}
        minRows={7}
        autosize
        aria-label="Paste your speech or script"
      />
      <Group justify="space-between" gap="sm" className="speech-time-live-count">
        <Text size="sm" c="dimmed">
          Word count updates instantly as you type.
        </Text>
        <Badge color="green" radius="sm" size="lg">
          {formatWords(wordCount)} words
        </Badge>
      </Group>
    </Stack>
  );
}

function WordsToTimeMode({
  wordCount,
  onWordCountChange,
}: {
  wordCount: NumberValue;
  onWordCountChange: (value: NumberValue) => void;
}) {
  return (
    <NumberInput
      label="Word Count"
      placeholder="Enter word count, e.g. 750"
      value={wordCount}
      min={0}
      allowNegative={false}
      onChange={(value) => onWordCountChange(toNumberInputValue(value))}
      aria-label="Word count"
    />
  );
}

function TimeToWordsMode({
  minutes,
  seconds,
  onMinutesChange,
  onSecondsChange,
}: {
  minutes: NumberValue;
  seconds: NumberValue;
  onMinutesChange: (value: NumberValue) => void;
  onSecondsChange: (value: NumberValue) => void;
}) {
  return (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
      <NumberInput
        label="Desired Speech Time - Minutes"
        placeholder="Minutes"
        value={minutes}
        min={0}
        allowNegative={false}
        onChange={(value) => onMinutesChange(toNumberInputValue(value))}
        aria-label="Desired speech time in minutes"
      />
      <NumberInput
        label="Desired Speech Time - Seconds"
        placeholder="Seconds"
        value={seconds}
        min={0}
        max={59}
        allowNegative={false}
        onChange={(value) => onSecondsChange(toNumberInputValue(value))}
        aria-label="Desired speech time in seconds"
      />
    </SimpleGrid>
  );
}

function SpeechSpeedSelector({
  speed,
  customWpm,
  onSpeedChange,
  onCustomWpmChange,
}: {
  speed: SpeechSpeedKey;
  customWpm: NumberValue;
  onSpeedChange: (value: SpeechSpeedKey) => void;
  onCustomWpmChange: (value: NumberValue) => void;
}) {
  return (
    <Stack gap="sm">
      <SimpleGrid cols={{ base: 1, sm: speed === "custom" ? 2 : 1 }} spacing="md">
        <Select
          label="Speaking Speed"
          data={speedOptions}
          value={speed}
          onChange={(value) =>
            onSpeedChange((value as SpeechSpeedKey) || "conversational")
          }
          allowDeselect={false}
          aria-label="Speaking speed"
        />
        {speed === "custom" ? (
          <NumberInput
            label="Custom WPM"
            placeholder="Enter words per minute"
            value={customWpm}
            min={1}
            allowNegative={false}
            onChange={(value) => onCustomWpmChange(toNumberInputValue(value))}
            aria-label="Custom words per minute"
          />
        ) : null}
      </SimpleGrid>
      <Text size="sm" c="dimmed" className="speech-time-helper">
        Most students speak at around 130-150 words per minute during
        presentations. Choose a slower speed if you pause often or explain
        complex points.
      </Text>
    </Stack>
  );
}

function SpeechTimeResult({
  result,
}: {
  result:
    | {
        mode: "text" | "words";
        wordCount: number;
        wordsPerMinute: number;
        formattedTime: string;
      }
    | {
        mode: "time";
        estimatedWords: number;
        wordsPerMinute: number;
        timeLabel: string;
      };
}) {
  return (
    <Box className="speech-time-result" aria-live="polite">
      <Stack gap="sm">
        <Title order={2}>Your Speech Time Estimate</Title>
        {result.mode === "text" ? (
          <>
            <Text>
              Your speech has <strong>{formatWords(result.wordCount)}</strong>{" "}
              words.
            </Text>
            <Text>
              At <strong>{result.wordsPerMinute} words per minute</strong>, it
              will take approximately <strong>{result.formattedTime}</strong>.
            </Text>
            <Text c="dimmed">
              If your presentation has slides, pauses, audience interaction, or
              examples, add 10-20% extra time.
            </Text>
          </>
        ) : null}

        {result.mode === "words" ? (
          <>
            <Text>
              <strong>{formatWords(result.wordCount)} words</strong> will take
              approximately <strong>{result.formattedTime}</strong> at{" "}
              {result.wordsPerMinute} words per minute.
            </Text>
            <Text c="dimmed">
              If your presentation has slides, pauses, audience interaction, or
              examples, add 10-20% extra time.
            </Text>
          </>
        ) : null}

        {result.mode === "time" ? (
          <Text>
            For a <strong>{result.timeLabel} speech</strong> at{" "}
            {result.wordsPerMinute} words per minute, you should write
            approximately <strong>{formatWords(result.estimatedWords)} words</strong>.
          </Text>
        ) : null}
      </Stack>
    </Box>
  );
}

function SpeechSpeedComparison({
  mode,
  wordCount,
  totalMinutes,
}: {
  mode: SpeechMode;
  wordCount: number;
  totalMinutes: number;
}) {
  const comparisonItems = (Object.keys(speakingSpeeds) as PresetSpeedKey[]).map(
    (key) => {
      const wpm = speakingSpeeds[key];
      const value =
        mode === "time"
          ? `${formatWords(totalMinutes * wpm)} words`
          : formatSpeechTime(wordCount / wpm);

      return {
        label: speedLabels[key],
        value,
      };
    },
  );

  return (
    <Box className="speech-time-comparison">
      <Stack gap="sm">
        <Title order={3}>Speed Comparison</Title>
        <ul>
          {comparisonItems.map((item) => (
            <li key={item.label}>
              <strong>{item.label}:</strong> {item.value}
            </li>
          ))}
        </ul>
      </Stack>
    </Box>
  );
}

function SpeechTimeSeoContent() {
  return (
    <>
      <SeoTextSection title="How Does the Speech Time Calculator Work?">
        <Text>
          The Speech Time Calculator estimates how long your speech will take by
          dividing your word count by your speaking speed. For example, if your
          speech has 750 words and you speak at 150 words per minute, your
          speech will take about 5 minutes.
        </Text>
      </SeoTextSection>

      <SeoTextSection title="How Many Words Are in a 5-Minute Speech?">
        <Text>
          A 5-minute speech is usually around 650 to 750 words, depending on
          speaking speed. A slower speaker may need around 500 words, while a
          faster speaker may speak around 900 words in 5 minutes.
        </Text>
      </SeoTextSection>

      <SeoTextSection title="How Many Words Are in a 3-Minute Speech?">
        <Text>
          A 3-minute speech is usually around 390 to 450 words at a normal
          speaking pace of 130 to 150 words per minute. If you speak slowly or
          pause often, aim for around 300 to 350 words.
        </Text>
      </SeoTextSection>

      <SeoTextSection title="How Many Words Are in a 10-Minute Speech?">
        <Text>
          A 10-minute speech is usually around 1300 to 1500 words at a normal
          speaking pace. If your presentation includes slides, examples, or
          audience interaction, it is better to keep the script closer to 1200
          to 1300 words.
        </Text>
      </SeoTextSection>

      <EstimateTable
        title="Common Words to Minutes Estimates"
        headers={[
          "Word Count",
          "Slow Pace 100 WPM",
          "Normal Pace 130 WPM",
          "Conversational Pace 150 WPM",
          "Fast Pace 180 WPM",
        ]}
        rows={wordsToMinutesRows}
      />

      <EstimateTable
        title="Common Speech Time to Word Count Estimates"
        headers={[
          "Speech Time",
          "Slow Pace 100 WPM",
          "Normal Pace 130 WPM",
          "Conversational Pace 150 WPM",
          "Fast Pace 180 WPM",
        ]}
        rows={timeToWordsRows}
      />

      <SeoTextSection title="Who Can Use This Speech Time Calculator?">
        <Text>
          This tool is useful for students, teachers, public speakers,
          presenters, content creators, podcast hosts, and video script writers.
          Students can use it to prepare class presentations, oral assignments,
          debates, seminars, and speeches with a clear time limit.
        </Text>
      </SeoTextSection>
    </>
  );
}

function SeoTextSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="speech-time-section">
      <Stack gap="md">
        <Title order={2}>{title}</Title>
        {children}
      </Stack>
    </section>
  );
}

function EstimateTable({
  title,
  headers,
  rows,
}: {
  title: string;
  headers: string[];
  rows: string[][];
}) {
  return (
    <section className="speech-time-section">
      <Stack gap="md">
        <Title order={2}>{title}</Title>
        <Box className="speech-time-table-wrap">
          <Table striped highlightOnHover withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                {headers.map((header) => (
                  <Table.Th key={header}>{header}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.join("-")}>
                  {row.map((cell) => (
                    <Table.Td key={cell}>{cell}</Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      </Stack>
    </section>
  );
}

function SpeechTimeCta() {
  return (
    <section className="speech-time-cta cta-box">
      <Group justify="space-between" align="center" gap="lg">
        <Group gap="md" align="flex-start" wrap="nowrap">
          <ThemeIcon radius="md" size={54} color="blue">
            <IconSchool size={28} />
          </ThemeIcon>
          <Stack gap={4}>
            <Title order={2}>Need Help Preparing Your Speech or Presentation?</Title>
            <Text c="dimmed" className="speech-time-cta-copy">
              If you know your speech timing but still need help with
              structure, research, slides, or editing, SubjectBuddy can connect
              you with academic support experts.
            </Text>
          </Stack>
        </Group>
        <Group gap="sm">
          <Button
            component={Link}
            to="/assignment-help"
            radius="md"
            className="cta-button"
            rightSection={<IconArrowRight size={18} />}
          >
            Get Assignment Help
          </Button>
          <Anchor
            component={Link}
            to="/presentation-help"
            className="secondary-link"
          >
            Get Presentation Help
          </Anchor>
        </Group>
      </Group>
    </section>
  );
}

function RelatedToolsSection() {
  const relatedItems = [
    {
      title: "Words to Pages Calculator",
      href: "/tools/words-to-pages-calculator",
      description: "Estimate how many pages your speech draft or assignment will make.",
      icon: <IconCalculator size={20} />,
    },
    {
      title: "Pages to Words Calculator",
      href: "/tools/pages-to-words-calculator",
      description: "Convert page limits into word count estimates for written work.",
      icon: <IconCalculator size={20} />,
    },
    {
      title: "Assignment Help",
      href: "/assignment-help",
      description: "Get academic support with research, structure, editing, and deadlines.",
      icon: <IconSchool size={20} />,
    },
    {
      title: "Essay Writing Help",
      href: "/essay-writing-help",
      description: "Plan and polish written assignments after checking length estimates.",
      icon: <IconFileText size={20} />,
    },
    {
      title: "Presentation Help",
      href: "/presentation-help",
      description: "Get help with presentation flow, slides, speaker notes, and timing.",
      icon: <IconFileText size={20} />,
    },
    {
      title: "How Many Words Is a 5-Minute Speech?",
      href: "/blog/how-many-words-is-a-5-minute-speech",
      description: "Use the supporting guide for 5-minute student speeches.",
      icon: <IconFileText size={20} />,
    },
    {
      title: "How to Write a Speech",
      href: "/blog/how-to-write-a-speech",
      description: "Review speech writing steps before rehearsing with a timer.",
      icon: <IconFileText size={20} />,
    },
    {
      title: "How to Make a Presentation",
      href: "/blog/how-to-make-a-presentation",
      description: "Prepare presentation structure, slides, and delivery notes.",
      icon: <IconFileText size={20} />,
    },
  ];

  // TODO: Add live aliases if /essay-writing-help, /presentation-help, or the
  // requested /blog speech guide slugs remain served under alternate routes.
  return (
    <section className="speech-time-section">
      <Stack gap="md">
        <Title order={2}>Related Tools and Guides</Title>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {relatedItems.map((item) => (
            <Paper
              key={item.href}
              component={Link}
              to={item.href}
              withBorder
              radius="md"
              p="lg"
              className="speech-time-link-card"
            >
              <Group gap="md" wrap="nowrap" align="flex-start">
                <ThemeIcon color="green" variant="light" radius="md">
                  {item.icon}
                </ThemeIcon>
                <Stack gap={4}>
                  <Text fw={800}>{item.title}</Text>
                  <Text size="sm" c="dimmed">
                    {item.description}
                  </Text>
                </Stack>
              </Group>
            </Paper>
          ))}
        </SimpleGrid>
      </Stack>
    </section>
  );
}

function SpeechTimeFAQ() {
  return (
    <section className="speech-time-section">
      <Stack gap="md">
        <Title order={2}>FAQs About Speech Time Calculator</Title>
        {faqs.map((faq) => (
          <Paper key={faq.question} withBorder radius="md" p="lg">
            <Stack gap={6}>
              <Title order={3}>{faq.question}</Title>
              <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                {faq.answer}
              </Text>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </section>
  );
}

export default SpeechTimeCalculatorPage;
